import type { IComponentsRegistrar } from '@wix/thunderbolt-components-loader'
import { getGlobalRegistryRuntime, getComponentsLibrariesFromViewerModel, getIsExperimentOpen } from './runtime'
import type { IRegistryRunAndReport } from './metrics'
import { getReportMetricName } from './metrics'
import type {
	IRegistryFactory,
	IThunderboltBuilderComponentModel,
	IThunderboltBuilderComponentsRegistry,
} from '@wix/editor-elements-registry/2.0/thunderboltBuilder'
import { getLoggerClient } from './logger'
import type {
	IThunderboltComponentModel,
	IThunderboltComponentsRegistry,
} from '@wix/editor-elements-registry/2.0/thunderbolt'

type ClientRegistryAPI = IThunderboltBuilderComponentsRegistry | IThunderboltComponentsRegistry
type ClientRegistryImpl = IThunderboltBuilderComponentModel | IThunderboltComponentModel

export interface IComponentsRegistryCSR {
	getRegistryAPI: () => ClientRegistryAPI
	getComponentsLibrariesAPI: () => IComponentsRegistrar
}

const METRIC_CREATE_REGISTRY = getReportMetricName({ host: 'viewer' })

export function createComponentsRegistryCSRFactory(registryImpl: IRegistryFactory<ClientRegistryImpl, never>) {
	return async function createComponentsRegistryCSR({
		runAndReport,
	}: IRegistryRunAndReport): Promise<IComponentsRegistryCSR> {
		if (window.componentsRegistry) {
			await window.componentsRegistry.runtimeReady
		}

		const runtime = getGlobalRegistryRuntime()
		const libraries = getComponentsLibrariesFromViewerModel()
		const isExperimentOpen = getIsExperimentOpen()

		const registryAPI = await runAndReport(METRIC_CREATE_REGISTRY, () =>
			registryImpl({
				options: {
					useScriptsInsteadOfEval: true,
					usePartialManifests: true,
				},
				mode: 'lazy',
				modes: {
					mobui: 'eager',
				},
				libraries: [...(runtime?.libraries || []), ...libraries],
				isExperimentOpen,
				getSentryClient: getLoggerClient(isExperimentOpen),
			})
		)

		let pending: Promise<void> | null = null
		return {
			getComponentsLibrariesAPI() {
				return {
					getComponents() {
						return registryAPI.getComponentsLoaders()
					},
					async getAllComponentsLoaders() {
						if (!pending) {
							pending = registryAPI.ensureManifestsAreLoaded()
						}
						await pending
						return registryAPI.getComponentsLoaders()
					},
				}
			},
			getRegistryAPI() {
				return registryAPI
			},
		}
	}
}
